import React, {FC} from 'react';
import styled from 'styled-components';

import avatarPlaceholderImage from '../../../../../../../src/assets/images/avatarPlaceholder.svg';
import {Avatar} from '../../../../../../../src/components/common/avatars/avatar';
import {AvatarBackground} from '../../../../../../../src/components/common/avatars/avatarBackground';
import {AccountPopoverContainer} from '../../../../../../../src/components/common/createFormInputs/accountPopoverContainer';
import {AvatarInput} from '../../../../../../../src/components/common/createFormInputs/avatarInput';
import {NamespaceDropdownBoxContainer} from '../../../../../../../src/components/common/dropdowns/namespace/namespaceDropdownBoxContainer';
import {addNewAccountAccountId} from '../../../../../../../src/helpers/models/cardAttributeUpdateHelpers';
import {computeIsGlobalNamespace} from '../../../../../../../src/helpers/models/namespaceHelpers';
import {PermissionsEnum} from '../../../../../../../src/models/api/roleModel';
import {ContactsManagerCreateContactFormState} from '../../../../../../../src/store/panels/contacts/contactsManager/sidePanel/contactsManagerCreateContactFormState';
import {
  CreateContactFormState,
  CreateContactLinkFieldModel,
} from '../../../../../../../src/store/panels/contacts/createContactForm/createContactFormState';
import {ContactsManagerPanelId} from '../../../../../../../src/store/panels/panelStateBase';
import {Localizer} from '../../../../../../../src/store/stateless/internationalizationSelectors';
import {useLocalize} from '../../../../../../../srcCommon/internationalization/internationalizationContext';
import {defineMessage, Trans} from '../../../../../../shared/helpers/src/internationalization/react';
import {useToggle} from '../../../../../../shared/helpers/src/react/hooks/hookHelpers';
import {Handler, HandlerOf} from '../../../../../../shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../shared/helpers/src/types/asyncStatuses';
import {TextAreaBox} from '../../../../../../shared/ui/src/components/boxedInputs/textAreaBox';
import {TextBox} from '../../../../../../shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../../../shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../../../../shared/ui/src/theme/userThemeProvider';
import {CreateContactCustomAttributesContainer} from './customAttributes/createContactCustomAttributesContainer';
import {CreateContactCardGroupInputContainer} from './formFields/createContactCardGroupInputContainer';
import {CreateContactInformationContainer} from './formFields/createContactInformationContainer';
import {CreateContactLinkInputContainer} from './formFields/createContactLinkInputContainer';

/*
 * Constants.
 */

const accessDropdownWidth = 260; // px.

/*
 * Props.
 */

interface CreateContactFormProps {
  panelId: ContactsManagerPanelId;
  createContactFormState: ContactsManagerCreateContactFormState;
  onUpdateName: HandlerOf<string>;
  onUpdateDescription: HandlerOf<string>;
  onUpdateAvatarFile: HandlerOf<File>;
  onUpdateAccess: HandlerOf<string>;
  onUpdateAccount: HandlerOf<number>;
}

/*
 * Styles.
 */

const StyledCreateContactFormDiv = styled.div`
  padding: 16px;
  overflow: auto;
`;

const StyledNameAndAvatarDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  width: 100%;
  padding-bottom: 12px;
`;

const StyledTextBoxContainerDiv = styled.div`
  width: 100%;
`;

const StyledAvatarIconDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledImage = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
  object-position: bottom;
  border-radius: 50%;
`;

const StyledAccessWrapperDiv = styled.div`
  padding-top: 8px;
  margin-bottom: -12px;
  display: flex;
  flex-flow: row;
`;

const StyledDescriptionWrapperDiv = styled.div`
  margin-top: 20px;
`;

const StyledLinksWrapperDiv = styled.div`
  margin-top: 20px;
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  margin-top: 15px;
  position: relative;
  left: -12px;
`;

/*
 * Component.
 */

export const CreateContactForm: FC<CreateContactFormProps> = (props) => {
  const theme = useUserTheme();
  const {
    panelId,
    createContactFormState,
    onUpdateName,
    onUpdateDescription,
    onUpdateAvatarFile,
    onUpdateAccess,
    onUpdateAccount,
  } = props;
  const localize = useLocalize();
  const [shouldShowAdditionalFields, toggleAdditionalFields] = useToggle(false);
  const isDisabled = createContactFormState.createStatus === AsyncStatusesEnum.LOADING;

  return (
    <StyledCreateContactFormDiv>
      <StyledNameAndAvatarDiv>
        <AvatarInput
          avatarFile={createContactFormState.avatarFile}
          isDisabled={isDisabled}
          updateAvatarFile={onUpdateAvatarFile}
          placeholder={
            <Avatar size={VisualSizesEnum.EXTRA_LARGE}>
              <AvatarBackground color={theme.greys.shade20} />
              <StyledAvatarIconDiv>
                <StyledImage src={avatarPlaceholderImage} />
              </StyledAvatarIconDiv>
            </Avatar>
          }
        />
        <StyledTextBoxContainerDiv>
          <TextBox
            size={VisualSizesEnum.LARGE}
            placeholder={localize(
              defineMessage({
                id: 'create-contact-form-name-title',
                message: 'Add name',
              }),
            )}
            onChange={(value) => onUpdateName(value)}
            isRequired
            isDisabled={isDisabled}
            noMargin
            shouldAutoFocus
          />
        </StyledTextBoxContainerDiv>
      </StyledNameAndAvatarDiv>

      {renderAccessField(createContactFormState.access, onUpdateAccess, isDisabled)}
      {renderAccountField(
        createContactFormState.accountId,
        isDisabled,
        onUpdateAccount,
        !computeIsGlobalNamespace(createContactFormState.access),
      )}
      <CreateContactInformationContainer panelId={panelId} isDisabled={isDisabled} />
      {renderDescription(createContactFormState.description, isDisabled, localize, onUpdateDescription)}
      <CreateContactCardGroupInputContainer
        panelId={panelId}
        namespace={createContactFormState.access}
        cardGroupIds={createContactFormState.cardGroupIds}
        isDisabled={isDisabled}
      />
      {renderAdditionalFields(
        panelId,
        shouldShowAdditionalFields,
        createContactFormState,
        isDisabled,
        toggleAdditionalFields,
      )}
    </StyledCreateContactFormDiv>
  );
};

/*
 * Helpers.
 */

function renderAdditionalFields(
  panelId: ContactsManagerPanelId,
  shouldShowAdditionalFields: boolean,
  createContactFormState: CreateContactFormState,
  isDisabled: boolean,
  toggleAdditionalFields: Handler,
) {
  return (
    <>
      {shouldShowAdditionalFields && (
        <>
          {renderLinks(createContactFormState.links, panelId, isDisabled)}
          <CreateContactCustomAttributesContainer panelId={panelId} isDisabled={isDisabled} />
        </>
      )}
      <StyledButtonWrapperDiv>
        <Button
          variant={ButtonVariantsEnum.LINK}
          size={VisualSizesEnum.SMALL}
          onClick={toggleAdditionalFields}
        >
          {shouldShowAdditionalFields ? (
            <Trans id="create-contact-form-show-less-additional-fields">Show less</Trans>
          ) : (
            <Trans id="create-contact-form-show-more-additional-fields">Show more</Trans>
          )}
        </Button>
      </StyledButtonWrapperDiv>
    </>
  );
}

function renderAccessField(access: string, onUpdateAccess: HandlerOf<string>, isDisabled: boolean) {
  return (
    <StyledAccessWrapperDiv>
      <NamespaceDropdownBoxContainer
        isDisabled={isDisabled}
        isRequired
        selectedNamespace={access}
        permission={PermissionsEnum.EDIT_CARD}
        shouldSkipTeamCheck
        shouldRenderSharedDropdownItem
        shouldRenderVisibilityDescription
        onNamespaceSelect={onUpdateAccess}
        dropdownWidth={accessDropdownWidth}
        resourceNamespace={undefined}
      />
    </StyledAccessWrapperDiv>
  );
}

function renderAccountField(
  accountId: number | undefined,
  isDisabled: boolean,
  onUpdateAccount: HandlerOf<number>,
  isPrivateNamespaceSelected: boolean,
) {
  return (
    <AccountPopoverContainer
      title={<Trans id="create-contact-form-account-title">Account</Trans>}
      accountId={accountId ?? addNewAccountAccountId}
      onSelect={onUpdateAccount}
      onDelete={() => onUpdateAccount(addNewAccountAccountId)}
      isDisabled={isDisabled || isPrivateNamespaceSelected}
      renderDisabledTooltip={() => maybeRenderAccountPopoverTooltip(isPrivateNamespaceSelected)}
    />
  );
}

function maybeRenderAccountPopoverTooltip(isPrivateNamespaceSelected: boolean) {
  if (!isPrivateNamespaceSelected) {
    return null;
  }
  return (
    <Trans id="create-contact-form-account-disabled">
      A private contact cannot be added to an Account because Accounts are shared across the company.
    </Trans>
  );
}

function renderDescription(
  description: string | undefined,
  isDisabled: boolean,
  localize: Localizer,
  onUpdateDescription: HandlerOf<string>,
) {
  return (
    <StyledDescriptionWrapperDiv>
      <TextAreaBox
        isDisabled={isDisabled}
        label={<Trans id="create-contact-form-description-title">Description</Trans>}
        size={VisualSizesEnum.MEDIUM}
        value={description}
        placeholder={localize(
          defineMessage({
            id: 'create-contact-form-description-placeholder',
            message: 'Add description',
          }),
        )}
        onChange={(value) => onUpdateDescription(value)}
        shouldAllowResize
        noMargin
      />
    </StyledDescriptionWrapperDiv>
  );
}

function renderLinks(
  links: ReadonlyArray<CreateContactLinkFieldModel>,
  panelId: ContactsManagerPanelId,
  isDisabled: boolean,
) {
  return (
    <StyledLinksWrapperDiv>
      {links.map((link, index) => (
        <CreateContactLinkInputContainer
          key={link.uid}
          panelId={panelId}
          link={link}
          isFirst={index === 0}
          isLast={index === links.length - 1}
          isDisabled={isDisabled}
        />
      ))}
    </StyledLinksWrapperDiv>
  );
}
